import React, { useState, useEffect, useRef, useCallback } from "react";
import { useGlobalLoader } from "../../context/GlobalLoaderContext";
import { getJobs, addAnswerKey, deleteAnswerKey, approveAnswerKey } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function HrAnswerKeyAdminPanel({ darkMode }) {
  const { loading: globalLoading } = useGlobalLoader();
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [answerKeys, setAnswerKeys] = useState([]);
  const [answerKeysOffset, setAnswerKeysOffset] = useState(0);
  const [answerKeysHasMore, setAnswerKeysHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Remove local isLoading, use global loader
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState({ jobSlug: "" });
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      await fetchThreeTableSection("answerKeys", false, { limit: PAGE_LIMIT, offset: 0 });
      const jobsRes = await getJobs();
      if (!ignore) setJobs(jobsRes.data);
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync answerKeys from global state
  useEffect(() => {
    setAnswerKeys((threeTable.answerKeys || []).slice());
    setAnswerKeysOffset(threeTable.answerKeys?.length || 0);
    setAnswerKeysHasMore((threeTable.answerKeys?.length || 0) === PAGE_LIMIT);
  }, [threeTable.answerKeys]);

  // Infinite scroll fetch
  const fetchMoreAnswerKeys = useCallback(async () => {
    if (!answerKeysHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("answerKeys", false, { limit: PAGE_LIMIT, offset: answerKeysOffset });
    setIsFetchingMore(false);
  }, [answerKeysHasMore, isFetchingMore, answerKeysOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!answerKeysHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreAnswerKeys();
    }
  }, [answerKeysHasMore, isFetchingMore, fetchMoreAnswerKeys]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleApprove = async (id) => {
    await approveAnswerKey(id);
    await fetchThreeTableSection("answerKeys", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addAnswerKey(formData);
    setFormData({ jobSlug: "" });
    await fetchThreeTableSection("answerKeys", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this answer key?")) {
      await deleteAnswerKey(id);
      await fetchThreeTableSection("answerKeys", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredAnswerKeys = answerKeys.filter((answerKey) => !formData.jobSlug || answerKey.jobSlug === formData.jobSlug);

  if (role !== "hr") {
    return <div className="admin-panel-error">❌ Access denied! Only HR can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--blue${darkMode ? " dark-mode" : ""}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">HR Answer Key Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Answer key entries ko cleaner workflow ke saath manage karo. Job choose karo, item add karo aur saved list me direct actions use karo.</p>
        </div>
        <div className="resource-admin-panel__pill">Answer Key</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Answer Key</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Answer Key Entries</h3>
        {globalLoading ? (
          <div className="global-loader"><div className="skeleton-loader" /></div>
        ) : (
        <ul>
          {filteredAnswerKeys.map((answerKey) => {
            const isPending = answerKey.status === "pending";
            const statusColor = isPending ? "#f7b500" : "#2ecc40";
            return (
              <li key={answerKey._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span>{getJobTitle(answerKey.jobSlug)}</span>
                <span style={{
                  background: statusColor,
                  color: "#fff",
                  borderRadius: 6,
                  padding: "2px 10px",
                  fontWeight: 600,
                  fontSize: 13
                }}>{answerKey.status === "pending" ? "Pending" : "Approved"}</span>
                {isPending && (
                  <>
                    <button onClick={() => handleApprove(answerKey._id)} style={{ background: "#f7b500", color: "#fff", border: "none", borderRadius: 4, padding: "2px 10px", marginLeft: 8 }}>Approve</button>
                    <button onClick={() => handleDelete(answerKey._id)} style={{ marginLeft: 8 }}>Delete</button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!answerKeysHasMore && !isFetchingMore && answerKeys.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more answer keys.</div>}
      </div>
    </div>
  );
}

export default HrAnswerKeyAdminPanel;
